import type {
  CreateStaffBody,
  StaffListResponse,
  StaffMember,
  UpdateStaffBody,
} from "@care-by-ella/contracts";
import { API_BASE, apiFetch, tenantHeader, withCredentials } from "./api";
import { jsonOr401 } from "./clinician-api";

// TES-491 — staff administration client (admin-only endpoints).

function commonHeaders(): Record<string, string> {
  return { "Content-Type": "application/json", ...tenantHeader() };
}

export class StaffApiError extends Error {}

async function jsonOrError<T>(res: Response): Promise<T> {
  if (res.status === 401 || res.status === 403) return jsonOr401<T>(res);
  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const body = (await res.json()) as { message?: unknown; error?: unknown };
      const msg = body.message ?? body.error;
      if (typeof msg === "string" && msg.trim()) detail = msg;
    } catch {
      // keep status-code detail
    }
    throw new StaffApiError(detail);
  }
  return (await res.json()) as T;
}

const BASE = `${API_BASE}/employee-api/v1/staff`;

export async function listStaff(): Promise<StaffListResponse> {
  const res = await apiFetch(BASE, {
    headers: commonHeaders(),
    ...withCredentials,
  });
  return jsonOrError(res);
}

export async function createStaff(body: CreateStaffBody): Promise<StaffMember> {
  const res = await apiFetch(BASE, {
    method: "POST",
    headers: commonHeaders(),
    body: JSON.stringify(body),
    ...withCredentials,
  });
  return jsonOrError(res);
}

export async function updateStaff(
  id: string,
  body: UpdateStaffBody,
): Promise<StaffMember> {
  const res = await apiFetch(`${BASE}/${id}`, {
    method: "PATCH",
    headers: commonHeaders(),
    body: JSON.stringify(body),
    ...withCredentials,
  });
  return jsonOrError(res);
}
